import { useEffect, useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "27659029122";

const BookingPage = () => {
  const [services, setServices] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  
  const [formData, setFormData] = useState({
    client_name: "",
    client_phone: "",
    client_email: "",
    service_id: "",
    service_name: "",
    booking_date: null,
    booking_time: "",
    notes: "",
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchTimeSlots = async () => {
      if (!formData.booking_date) return;
      
      try {
        const dateStr = format(formData.booking_date, "yyyy-MM-dd");
        const response = await axios.get(`${API}/timeslots/${dateStr}`);
        setTimeSlots(response.data.slots || []);
        if (response.data.message) {
          toast.info(response.data.message);
        }
      } catch (error) {
        console.error("Error fetching time slots:", error);
        toast.error("Failed to load available times");
      }
    };
    fetchTimeSlots();
  }, [formData.booking_date]);

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setFormData(prev => ({
      ...prev,
      service_id: serviceId,
      service_name: service?.name || "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.client_name || !formData.client_phone || !formData.service_id || !formData.booking_date || !formData.booking_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    try {
      const bookingData = {
        ...formData,
        booking_date: format(formData.booking_date, "yyyy-MM-dd"),
      };
      
      const response = await axios.post(`${API}/bookings`, bookingData);
      setBookingDetails(response.data);
      setBookingSuccess(true);
      toast.success("Booking confirmed!");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const sendWhatsAppConfirmation = () => {
    if (!bookingDetails) return;
    
    const message = encodeURIComponent(
      `Hi! I've just booked an appointment:\n\nName: ${bookingDetails.client_name}\nService: ${bookingDetails.service_name}\nDate: ${bookingDetails.booking_date}\nTime: ${bookingDetails.booking_time}\n\nPlease confirm my booking. Thank you!`
    );
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, "_blank");
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date < today) return true;
    if (date.getDay() === 0) return true;
    return false;
  };

  if (bookingSuccess && bookingDetails) {
    return (
      <div className="min-h-screen bg-[#FFFCF9] py-20" data-testid="booking-success">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="rounded-2xl border-0 spa-shadow overflow-hidden">
            <div className="bg-spa-pink p-8 text-center">
              <CheckCircle className="h-16 w-16 text-white mx-auto mb-4" />
              <h2 className="font-heading text-3xl text-white">Booking Confirmed!</h2>
            </div>
            <CardContent className="p-8">
              <div className="space-y-4 mb-8">
                {[
                  { label: "Name", value: bookingDetails.client_name },
                  { label: "Service", value: bookingDetails.service_name },
                  { label: "Date", value: bookingDetails.booking_date },
                  { label: "Time", value: bookingDetails.booking_time },
                ].map((item) => (
                  <div key={item.label} className="flex justify-between py-3 border-b border-spa-neutral-200">
                    <span className="text-spa-neutral-500">{item.label}</span>
                    <span className="font-medium text-spa-neutral-800">{item.value}</span>
                  </div>
                ))}
              </div>
              
              <div className="space-y-4">
                <Button
                  onClick={sendWhatsAppConfirmation}
                  className="w-full bg-[#25D366] hover:bg-[#20BA5C] text-white rounded-lg py-6 font-medium"
                  data-testid="whatsapp-confirm-btn"
                >
                  Confirm via WhatsApp
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setBookingSuccess(false);
                    setFormData({
                      client_name: "", client_phone: "", client_email: "",
                      service_id: "", service_name: "", booking_date: null,
                      booking_time: "", notes: "",
                    });
                  }}
                  className="w-full rounded-lg py-6 font-medium border-2 border-spa-pink text-spa-pink-600 hover:bg-spa-pink hover:text-white"
                  data-testid="book-another-btn"
                >
                  Book Another Appointment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFCF9]" data-testid="booking-page">
      {/* Hero */}
      <section className="py-20 bg-spa-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="text-spa-pink-500 font-medium text-sm tracking-widest uppercase">
              Book Online
            </span>
            <h1 className="font-heading text-5xl md:text-6xl text-spa-neutral-800 mt-4 mb-6">
              Schedule Your Visit
            </h1>
            <p className="text-spa-neutral-500 text-lg max-w-2xl mx-auto">
              Choose your preferred service, date, and time. We'll confirm your appointment shortly.
            </p>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="rounded-2xl border-0 spa-shadow">
            <CardHeader className="p-8 pb-0">
              <CardTitle className="font-heading text-2xl text-spa-neutral-800">
                Appointment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Personal Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-spa-neutral-700">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Your full name"
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      className="rounded-lg border-spa-neutral-200 h-12"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-spa-neutral-700">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="Your phone number"
                      value={formData.client_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                      className="rounded-lg border-spa-neutral-200 h-12"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-spa-neutral-700">Email (Optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Your email address"
                    value={formData.client_email}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                    className="rounded-lg border-spa-neutral-200 h-12"
                    data-testid="input-email"
                  />
                </div>

                {/* Service Selection */}
                <div className="space-y-2">
                  <Label className="text-spa-neutral-700">Select Service *</Label>
                  <Select onValueChange={handleServiceChange} data-testid="select-service">
                    <SelectTrigger className="rounded-lg border-spa-neutral-200 h-12">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} ({service.duration} min)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date Selection */}
                <div className="space-y-2">
                  <Label className="text-spa-neutral-700">Select Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal rounded-lg border-spa-neutral-200 h-12",
                          !formData.booking_date && "text-spa-neutral-400"
                        )}
                        data-testid="date-picker-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.booking_date ? format(formData.booking_date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={formData.booking_date}
                        onSelect={(date) => setFormData(prev => ({ ...prev, booking_date: date, booking_time: "" }))}
                        disabled={isDateDisabled}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Time Selection */}
                {formData.booking_date && (
                  <div className="space-y-2">
                    <Label className="text-spa-neutral-700">Select Time *</Label>
                    {timeSlots.length === 0 ? (
                      <p className="text-spa-neutral-500 text-sm">No available times for this date</p>
                    ) : (
                      <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                        {timeSlots.map((slot) => (
                          <Button
                            key={slot.time}
                            type="button"
                            variant={formData.booking_time === slot.time ? "default" : "outline"}
                            disabled={!slot.available}
                            onClick={() => setFormData(prev => ({ ...prev, booking_time: slot.time }))}
                            className={cn(
                              "rounded-lg text-sm",
                              formData.booking_time === slot.time 
                                ? "bg-spa-pink hover:bg-spa-pink-600 text-white" 
                                : "border-spa-neutral-200 hover:border-spa-pink",
                              !slot.available && "opacity-50 cursor-not-allowed"
                            )}
                            data-testid={`time-slot-${slot.time.replace(':', '')}`}
                          >
                            {slot.time}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes" className="text-spa-neutral-700">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    placeholder="Any special requests or notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="rounded-lg border-spa-neutral-200 min-h-24"
                    data-testid="input-notes"
                  />
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg py-6 text-lg font-medium transition-all hover:shadow-lg"
                  data-testid="submit-booking-btn"
                >
                  {loading ? "Booking..." : "Confirm Booking"}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mt-8 text-center">
            <p className="text-spa-neutral-500 text-sm">
              By booking, you agree to our{" "}
              <a href="/terms" className="text-spa-pink-600 hover:underline">Terms & Conditions</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
